import React, { memo } from 'react';
import { Box, Chip, IconButton, Paper, Tooltip, Typography, alpha } from '@mui/material';
import {
  EditOutlined as EditIcon,
  DeleteOutline as DeleteIcon,
  VisibilityOutlined as ViewIcon,
  CheckRounded,
} from '@mui/icons-material';
import { Question, QuestionType, PaginationState, GetImportanceColorFn } from './types';
import MathTextRenderer from '../shared/MathTextRenderer';
import AnswerBadge from '../shared/AnswerBadge';
import { formatNumber } from '../../utils/paperNumbering';

interface QuestionCardProps {
  question: Question;
  index: number;
  pagination: PaginationState;
  canWrite: boolean;
  onView: (question: Question) => void;
  onEdit: (question: Question) => void;
  onDelete: (questionId: string) => void;
  getImportanceColor: GetImportanceColorFn;
  /** Name the question's chapter (results from a bank-wide search). */
  showChapter?: boolean;
}

/** Urdu / Arabic script: the question and its options read right to left. */
const RTL_SCRIPT = /[\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF]/;

const chipSx = { height: 22, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.9 } } as const;

/** One question in the bank: the question as it reads, its answer key, and its details. */
const QuestionCard = memo<QuestionCardProps>(({
  question,
  index,
  pagination,
  canWrite,
  onView,
  onEdit,
  onDelete,
  getImportanceColor,
  showChapter,
}) => {
  const questionNumber = ((pagination.page || 1) - 1) * (pagination.limit || 10) + index + 1;
  const isMcq = question.type === QuestionType.MCQ;
  const tags = question.metadata?.tags || [];
  const pastPapers = question.metadata?.pastPaperReferences || [];
  const dir = RTL_SCRIPT.test(question.questionText) ? 'rtl' : 'ltr';
  const chapterName = showChapter && typeof question.chapterId !== 'string' ? question.chapterId?.name : null;

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        position: 'relative',
        borderRadius: '12px',
        p: 2.25,
        transition: 'border-color 150ms, box-shadow 150ms',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.4),
          boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.06)}`,
        },
        '&:hover .qc-actions, &:focus-within .qc-actions': { opacity: 1 },
      })}
    >
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        <Box
          sx={(theme) => ({
            flexShrink: 0,
            minWidth: 30,
            height: 26,
            px: 0.75,
            borderRadius: '8px',
            display: 'grid',
            placeItems: 'center',
            fontSize: '0.75rem',
            fontWeight: 700,
            color: 'primary.main',
            bgcolor: alpha(theme.palette.primary.main, 0.08),
          })}
        >
          {questionNumber}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {chapterName && (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 600, mb: 0.25 }}>
              {chapterName}
            </Typography>
          )}
          <Typography variant="body1" dir={dir} sx={{ fontSize: '0.95rem', lineHeight: 1.6, pt: 0.1, pr: { sm: 12 } }}>
            <MathTextRenderer text={question.questionText} />
          </Typography>

          {/* MCQ options, the correct one marked */}
          {isMcq && question.options && question.options.length > 0 && (
            <Box dir={dir} sx={{ mt: 1.25, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.75 }}>
              {question.options.map((option, idx) => {
                const correct = !!question.correctAnswer && option === question.correctAnswer;
                return (
                  <Box
                    key={idx}
                    sx={(theme) => ({
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 1,
                      px: 1.25,
                      py: 0.6,
                      borderRadius: '8px',
                      fontSize: '0.85rem',
                      border: '1px solid',
                      borderColor: correct ? alpha(theme.palette.success.main, 0.5) : 'divider',
                      bgcolor: correct ? alpha(theme.palette.success.main, 0.08) : 'transparent',
                      color: correct ? 'text.primary' : 'text.secondary',
                    })}
                  >
                    <Box component="span" sx={{ fontWeight: 700, color: correct ? 'success.main' : 'text.disabled' }}>
                      {String.fromCharCode(65 + idx)}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}><MathTextRenderer text={option} /></Box>
                    {correct && <CheckRounded sx={{ fontSize: 16, color: 'success.main', alignSelf: 'center' }} />}
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Sub-parts */}
          {question.subParts && question.subParts.length > 0 && (
            <Box dir={dir} sx={{ mt: 1 }}>
              {question.subParts.map((part, idx) => (
                <Typography key={idx} variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                  <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', mr: 0.75 }}>
                    ({formatNumber(idx + 1, 'lowerRoman')})
                  </Box>
                  <MathTextRenderer text={part.text} />
                </Typography>
              ))}
            </Box>
          )}

          {/* Fill in the blanks: the expected word, inline */}
          {!isMcq && question.correctAnswer && (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              <Box component="span" sx={{ fontWeight: 600, color: 'success.main', mr: 0.75 }}>Answer</Box>
              <MathTextRenderer text={question.correctAnswer} />
            </Typography>
          )}

          {/* Details */}
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
            <Chip label={question.type} size="small" variant="outlined" sx={chipSx} />
            <Chip label={question.importance} size="small" color={getImportanceColor(question.importance)} variant="outlined" sx={chipSx} />
            {!!question.metadata?.marks && (
              <Chip label={`${question.metadata.marks} mark${question.metadata.marks === 1 ? '' : 's'}`} size="small" sx={{ ...chipSx, fontWeight: 600 }} />
            )}
            {!!question.metadata?.estimatedTime && (
              <Chip label={`${question.metadata.estimatedTime} min`} size="small" sx={chipSx} />
            )}
            <AnswerBadge answer={question.answer} answerImages={question.answerImages} />
            {(tags.length > 0 || pastPapers.length > 0) && <Box sx={{ flex: 1 }} />}
            {tags.map((tag, idx) => (
              <Typography key={`t${idx}`} variant="caption" sx={{ color: 'primary.main', fontWeight: 500 }}>
                #{tag}
              </Typography>
            ))}
            {pastPapers.map((ref, idx) => (
              <Chip key={`p${idx}`} label={`Past paper ${ref}`} size="small" color="secondary" variant="outlined" sx={chipSx} />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Actions: quiet until the card is hovered or focused */}
      <Box
        className="qc-actions"
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          display: 'flex',
          gap: 0.25,
          opacity: { xs: 1, md: 0.35 },
          transition: 'opacity 150ms',
          '& .MuiIconButton-root': { color: 'text.secondary' },
        }}
      >
        <Tooltip title="View">
          <IconButton size="small" onClick={() => onView(question)} sx={{ '&:hover': { color: 'primary.main' } }}>
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {canWrite && (
          <>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => onEdit(question)} sx={{ '&:hover': { color: 'primary.main' } }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" onClick={() => onDelete(question._id)} sx={{ '&:hover': { color: 'error.main' } }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </Paper>
  );
});

QuestionCard.displayName = 'QuestionCard';

export default QuestionCard;
